'use client';

/**
 * DataRoomDocumentRow Component
 *
 * Renders a single document row within the data room.
 * Shows document name, type, scope badge, verification status, and action buttons.
 */

import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CloudUpload from '@mui/icons-material/CloudUpload';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import LinkOff from '@mui/icons-material/LinkOff';
import Replay from '@mui/icons-material/Replay';
import Schedule from '@mui/icons-material/Schedule';
import Visibility from '@mui/icons-material/Visibility';
import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useI18n } from '../../hooks/useI18n';
import { iconSizes } from '../../theme/typography';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassIconButton } from '../GlassIconButton/GlassIconButton';
import { type DocumentScope, DocumentScopeBadge } from './DocumentScopeBadge';

// ============================================================================
// Types
// ============================================================================

export type VerificationStatus = 'verified' | 'pending' | 'rejected' | 'expired' | 'needs_reupload';

export interface DataRoomDocumentRowProps {
  /** Unique document identifier (optional for placeholder rows) */
  documentId?: string;
  /** Document display name */
  name: string;
  /** Document type label */
  documentType: string;
  /** Document category */
  category?: string;
  /** Document scope */
  scope: DocumentScope;
  /** Verification status of the document */
  verificationStatus?: VerificationStatus;
  /** Rejection or reupload reason (shown when status is rejected or needs_reupload) */
  rejectionReason?: string;
  /** Source label for provenance display */
  sourceLabel?: string;
  /** Whether this row is a placeholder for an unfulfilled requirement (Issue #1145) */
  isPlaceholder?: boolean;
  /** Checklist item ID for placeholder rows (Issue #1145) */
  checklistItemId?: string;
  /** Requirement label shown on fulfilled rows (Issue #1145) */
  requirementLabel?: string;
  /** Whether this is a required document (Issue #1145) */
  isRequired?: boolean;
  /**
   * Issue #1631 M4 — pre-resolved human label for the fund-attribution
   * badge ("Goldman · Line A · DSCR" or generic "warehouse line"). Renders
   * as a small GlassChip next to the row name when set.
   */
  fundAttributionLabel?: string | null;
  /** Upload action for placeholder rows (Issue #1145) */
  onUploadForRequirement?: (checklistItemId: string, documentType: string) => void;
  /** Callback when view button is clicked */
  onView?: (documentId: string) => void;
  /** Callback when unlink button is clicked */
  onUnlink?: (documentId: string) => void;
  /** Whether to show the unlink button */
  showUnlink?: boolean;
  /** Callback when verify button is clicked */
  onVerify?: (documentId: string) => void;
  /** Callback when reject button is clicked */
  onReject?: (documentId: string) => void;
  /** Callback when request reupload button is clicked */
  onRequestReupload?: (documentId: string) => void;
  /** Whether to show verification action controls */
  showVerificationControls?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

const VERIFICATION_CONFIG: Record<
  VerificationStatus,
  { icon: React.ReactElement; color: 'success' | 'warning' | 'error' | 'default'; label: string }
> = {
  verified: {
    icon: <CheckCircle sx={{ fontSize: iconSizes.sm }} />,
    color: 'success',
    label: 'status.verified',
  },
  pending: {
    icon: <HourglassEmpty sx={{ fontSize: iconSizes.sm }} />,
    color: 'warning',
    label: 'status.pending',
  },
  rejected: {
    icon: <Warning sx={{ fontSize: iconSizes.sm }} />,
    color: 'error',
    label: 'status.rejected',
  },
  expired: {
    icon: <Schedule sx={{ fontSize: iconSizes.sm }} />,
    color: 'default',
    label: 'status.expired',
  },
  needs_reupload: {
    icon: <Replay sx={{ fontSize: iconSizes.sm }} />,
    color: 'warning',
    label: 'status.needsReupload',
  },
};

/** Statuses where verification action buttons should be shown */
const ACTIONABLE_STATUSES = new Set<VerificationStatus>(['pending', 'rejected', 'needs_reupload']);

// ============================================================================
// Component
// ============================================================================

export const DataRoomDocumentRow = ({
  documentId,
  name,
  documentType,
  category,
  scope,
  verificationStatus,
  rejectionReason,
  sourceLabel,
  isPlaceholder,
  checklistItemId,
  requirementLabel,
  isRequired,
  fundAttributionLabel,
  onUploadForRequirement,
  onView,
  onUnlink,
  showUnlink = true,
  onVerify,
  onReject,
  onRequestReupload,
  showVerificationControls = false,
}: DataRoomDocumentRowProps): React.ReactElement => {
  const { t } = useI18n();

  const isPlaceholderRow = isPlaceholder && !documentId;
  const verificationConfig = verificationStatus ? VERIFICATION_CONFIG[verificationStatus] : null;
  const reasonDisplay =
    rejectionReason && (verificationStatus === 'rejected' || verificationStatus === 'needs_reupload')
      ? {
          color: verificationStatus === 'needs_reupload' ? 'warning.main' : 'error.main',
          key:
            verificationStatus === 'needs_reupload' ? 'documents.reuploadRequest.reason' : 'documents.rejection.reason',
        }
      : null;

  return (
    <Box
      data-hover
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1,
        px: 1.5,
        borderRadius: 1,
        ...(isPlaceholderRow && {
          border: '1px dashed',
          borderColor: 'divider',
          opacity: 0.85,
          bgcolor: 'action.hover',
          mb: 0.5,
        }),
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Placeholder icon */}
      {isPlaceholderRow && <DescriptionOutlined sx={{ fontSize: iconSizes.base, color: 'text.disabled', mr: 1 }} />}
      {/* Document info */}
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: isPlaceholderRow ? 400 : 500,
              color: isPlaceholderRow ? 'text.secondary' : 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
          {!isPlaceholderRow && <DocumentScopeBadge scope={scope} />}
          {isRequired && (
            <GlassChip
              label={t('dataRoom.required')}
              size="small"
              color="warning"
              variant="outlined"
              glowOnHover={false}
            />
          )}
          {fundAttributionLabel && (
            <GlassChip
              label={t('loans.documents.requiredByFund.badge', { fund: fundAttributionLabel })}
              size="small"
              color="info"
              variant="outlined"
              glowOnHover={false}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {documentType}
          </Typography>
          {category && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
              }}
            >
              {category}
            </Typography>
          )}
          {sourceLabel && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
              }}
            >
              {t('dataRoom.linkedFrom', { source: sourceLabel })}
            </Typography>
          )}
        </Stack>

        {/* Requirement label on fulfilled rows */}
        {!isPlaceholderRow && requirementLabel && (
          <Typography
            variant="caption"
            sx={{
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            {requirementLabel}
          </Typography>
        )}

        {reasonDisplay && (
          <Typography
            variant="caption"
            sx={{
              color: reasonDisplay.color,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {t(reasonDisplay.key, { reason: rejectionReason })}
          </Typography>
        )}
      </Stack>
      {/* Placeholder: missing status chip */}
      {isPlaceholderRow && (
        <GlassChip
          label={t('status.missing')}
          color="default"
          size="small"
          variant="outlined"
          glowOnHover={false}
          sx={{ mx: 1 }}
        />
      )}
      {/* Verification badge (non-placeholder rows only) */}
      {!isPlaceholderRow && verificationConfig && (
        <GlassChip
          icon={verificationConfig.icon}
          label={t(verificationConfig.label)}
          color={verificationConfig.color}
          size="small"
          variant="outlined"
          glowOnHover={false}
          sx={{ mx: 1 }}
        />
      )}
      {/* Actions */}
      <Stack direction="row" spacing={0.5}>
        {/* Placeholder: upload button */}
        {isPlaceholderRow && onUploadForRequirement && checklistItemId && (
          <GlassIconButton
            size="small"
            variant="standard"
            color="primary"
            tooltip={t('common.upload')}
            aria-label={t('common.upload')}
            onClick={() => onUploadForRequirement(checklistItemId, documentType)}
          >
            <CloudUpload />
          </GlassIconButton>
        )}

        {/* Non-placeholder actions */}
        {!isPlaceholderRow && onView && documentId && (
          <GlassIconButton
            size="small"
            variant="standard"
            tooltip={t('common.view')}
            aria-label={t('common.view')}
            onClick={() => onView(documentId)}
          >
            <Visibility />
          </GlassIconButton>
        )}

        {/* Verification action buttons — after View, before Unlink */}
        {!isPlaceholderRow &&
          showVerificationControls &&
          verificationStatus &&
          ACTIONABLE_STATUSES.has(verificationStatus) &&
          documentId && (
            <>
              {onVerify && (
                <GlassIconButton
                  size="small"
                  variant="standard"
                  color="success"
                  tooltip={t('action.verify')}
                  aria-label={t('action.verify')}
                  onClick={() => onVerify(documentId)}
                >
                  <CheckCircle />
                </GlassIconButton>
              )}
              {onReject && (
                <GlassIconButton
                  size="small"
                  variant="standard"
                  color="error"
                  tooltip={t('action.reject')}
                  aria-label={t('action.reject')}
                  onClick={() => onReject(documentId)}
                >
                  <Cancel />
                </GlassIconButton>
              )}
              {onRequestReupload && (
                <GlassIconButton
                  size="small"
                  variant="standard"
                  color="warning"
                  tooltip={t('action.requestReupload')}
                  aria-label={t('action.requestReupload')}
                  onClick={() => onRequestReupload(documentId)}
                >
                  <Replay />
                </GlassIconButton>
              )}
            </>
          )}

        {!isPlaceholderRow && showUnlink && onUnlink && documentId && (
          <GlassIconButton
            size="small"
            variant="standard"
            color="error"
            tooltip={t('dataRoom.unlinkDocument')}
            aria-label={t('dataRoom.unlinkDocument')}
            onClick={() => onUnlink(documentId)}
          >
            <LinkOff />
          </GlassIconButton>
        )}
      </Stack>
    </Box>
  );
};

DataRoomDocumentRow.displayName = 'DataRoomDocumentRow';

export default DataRoomDocumentRow;
